import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { EyeOff, AlertTriangle, Wrench } from "lucide-react";
import type { PageIssue, IndexabilityDetails, IndexabilityFix } from "@/types/technicalAudit";
import {
	classifyIndexabilityCause,
	INDEXABILITY_CAUSE_META,
	type IndexabilityCause,
} from "./indexabilityCause";

interface IndexabilityCardProps {
	issue: PageIssue;
}

export function IndexabilityCard({ issue }: IndexabilityCardProps) {
	const details = issue.issue_details as IndexabilityDetails;
	const proposedFix = issue.proposed_fix as IndexabilityFix | null;

	// B1: identify WHY the page is non-indexable before showing any fix. Prefer a
	// cause the backend already attached, else classify from the crawl signals.
	const cause: IndexabilityCause =
		(proposedFix?.indexability_cause as IndexabilityCause) ||
		(details.indexability_cause as IndexabilityCause) ||
		classifyIndexabilityCause(issue.status_code, details.indexability_status);
	const causeMeta = INDEXABILITY_CAUSE_META[cause] ?? INDEXABILITY_CAUSE_META.other;

	return (
		<div className="space-y-4">
			{/* Critical Banner */}
			<div className="flex items-start gap-3 rounded-xl p-4 border bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800">
				<div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-red-100 dark:bg-red-900/40">
					<AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
				</div>
				<div className="flex-1">
					<h4 className="font-semibold text-red-900 dark:text-red-300">Indexability Issue</h4>
					<p className="text-sm mt-1 text-red-700 dark:text-red-400">
						This page is not indexable by search engines. It will not appear in search results
						until the issue is resolved.
					</p>
				</div>
			</div>

			{/* Status Card */}
			<Card className="border-border shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-red-50 dark:bg-red-950/40 flex items-center justify-center">
							<EyeOff className="w-4 h-4 text-red-600 dark:text-red-400" />
						</div>
						Indexability Status
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0 space-y-4">
					<div className="flex items-center gap-3">
						<Badge variant="outline" className="bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800">
							{details.indexability_status}
						</Badge>
					</div>
					<div className="space-y-1.5">
						<div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
							Root cause
						</div>
						<div className="p-3 bg-muted rounded-lg border border-border space-y-2">
							<div className="flex items-center gap-2">
								<Badge variant="outline" className="bg-muted text-foreground border-border">
									{causeMeta.label}
								</Badge>
								<Badge
									variant="outline"
									className={
										causeMeta.redirectEligible
											? "bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800"
											: "bg-blue-50 dark:bg-blue-950/40 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-800"
									}
								>
									{causeMeta.redirectEligible
										? "Eligible for an automatic 301 redirect"
										: "Review-only — will not be auto-redirected"}
								</Badge>
							</div>
							<p className="text-sm text-foreground">
								{details.reason || causeMeta.explanation}
							</p>
							{cause === "canonicalised" && details.canonical_url && (
								<p className="text-xs text-muted-foreground">
									Canonical target: {details.canonical_url}
								</p>
							)}
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Proposed Fix */}
			{proposedFix && (
				<Card className="border-green-200 dark:border-green-800 shadow-sm bg-green-50 dark:bg-green-950/40/30">
					<CardHeader className="pb-3">
						<CardTitle className="text-sm font-semibold text-green-900 dark:text-green-300 flex items-center gap-2">
							<div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
								<Wrench className="w-4 h-4 text-green-600 dark:text-green-400" />
							</div>
							Recommended Action
						</CardTitle>
					</CardHeader>
					<CardContent className="pt-0 space-y-3">
						<div className="p-3 bg-card rounded-lg border border-green-200 dark:border-green-800">
							<p className="text-sm text-green-800 dark:text-green-300 font-medium">
								{proposedFix.recommended_action}
							</p>
						</div>
						{proposedFix.reasoning && (
							<div className="p-3 bg-card rounded-lg border border-green-200 dark:border-green-800">
								<p className="text-sm text-green-800 dark:text-green-300">{proposedFix.reasoning}</p>
							</div>
						)}
					</CardContent>
				</Card>
			)}
		</div>
	);
}
